import { useCallback, useRef } from 'react';

import { useFileAttachment } from '~hooks/useFileAttachment/useFileAttachment';
import useTimesheets from '~hooks/useTimesheets/useTimesheets';
import {
  fileAttachmentToListItem,
  TimesheetListItem,
  timesheetToListItem,
} from '~pages/Approvals/shared/timesheetListItem';

// Fetches a job's timesheets normalized to TimesheetListItem rows. Owns no state — the
// caller holds the list + loading. `use_new_timesheet` OFF reads legacy FileAttachments
// in the Timesheet category; ON dual-reads those too, so sheets uploaded before the flag
// was switched on do not disappear.
export default function useJobTimesheets() {
  const { getTimesheetsOrEmpty } = useTimesheets();
  const { getAllFileAttachmentsByJobId } = useFileAttachment();
  // useFileAttachment returns unmemoized functions, so hold the latest in a ref: listing
  // it as a dependency would destabilize the callback and loop the caller's fetch effect,
  // while capturing it in the closure would pin the first render's copy.
  const getAllFileAttachmentsByJobIdRef = useRef(getAllFileAttachmentsByJobId);
  getAllFileAttachmentsByJobIdRef.current = getAllFileAttachmentsByJobId;

  const fetchJobTimesheets = useCallback(
    async (
      companyId: string,
      jobId: string,
      useNewTimesheet: boolean,
      signal?: AbortSignal,
    ): Promise<TimesheetListItem[]> => {
      // Letting the attachments call's rejection escape would drop the new-model rows too.
      const legacyRequest = getAllFileAttachmentsByJobIdRef
        .current(jobId, signal)
        .then((attachments) =>
          (attachments ?? [])
            .filter((attachment) => attachment.category === 'Timesheet')
            .map(fileAttachmentToListItem),
        )
        .catch((error): TimesheetListItem[] => {
          if (signal?.aborted) {
            throw error;
          }

          return [];
        });

      if (!useNewTimesheet) {
        return legacyRequest;
      }
      // Neither request depends on the other, so they run together rather than paying
      // both round-trips in sequence.
      const [legacyRows, rows] = await Promise.all([
        legacyRequest,
        getTimesheetsOrEmpty(companyId, { jobId, signal }),
      ]);
      return [...rows.map(timesheetToListItem), ...legacyRows];
    },
    [getTimesheetsOrEmpty],
  );

  return { fetchJobTimesheets };
}
